// Profil, the account tab. Round 7 reduced it to four things: who you are,
// the way into Edit Profil, every feature the five slot tab bar cannot
// hold, and sign out.
//
// What left, and why: the name, bio, photo, language and default privacy
// controls all moved into Edit Profil, so a setting about the traveller has
// exactly one home. "Items waiting to sync" was removed outright, because
// SyncBadge already reports pending work in the header where it is
// actionable, and a second copy buried in settings is noise.
import { useEffect } from "react";
import {
  Icon,
  IconBook,
  IconBookmark,
  IconCalendar,
  IconChart,
  IconChevronRight,
  IconLogout,
  IconMap,
  IconPen,
  IconPlan,
  IconRoute,
  IconWallet,
} from "@traveldiary/ui";
import { TravellerAvatar } from "../components/TravellerAvatar";
import type { ReactNode } from "react";
import { hrefFor, type RouteName } from "../lib/router";
import type { TranslationKey } from "../lib/i18n";
import { useApp } from "../state/app";

interface FeatureLink {
  route: RouteName;
  param?: string;
  labelKey: TranslationKey;
  glyph: ReactNode;
}

const FEATURES: FeatureLink[] = [
  { route: "map", labelKey: "nav.map", glyph: <IconMap /> },
  { route: "diary", labelKey: "diary.title", glyph: <IconBook /> },
  { route: "trips", labelKey: "trips.title", glyph: <IconRoute /> },
  { route: "itinerary", labelKey: "itinerary.title", glyph: <IconPlan /> },
  { route: "diary", param: "calendar", labelKey: "calendar.title", glyph: <IconCalendar /> },
  { route: "budget", labelKey: "budget.title", glyph: <IconWallet /> },
  { route: "stats", labelKey: "stats.title", glyph: <IconChart /> },
  { route: "wishlist", labelKey: "wishlist.title", glyph: <IconBookmark /> },
];

export function ProfileScreen() {
  const { t, me, authUser, displayName, refreshProfile, signOut } = useApp();
  const user = me ?? authUser;

  useEffect(() => {
    void refreshProfile();
  }, [refreshProfile]);

  return (
    <div className="screen-pad">
      <section className="section">
        <h2 className="section-title">{t("profile.identity")}</h2>

        <div className="profile-head">
          <TravellerAvatar user={user} fallbackName={displayName} size="lg" isSelf />
          <div className="profile-head-text">
            <p className="profile-name">{user?.displayName ?? displayName}</p>
            <p className="profile-handle">{user?.username ?? ""}</p>
          </div>
        </div>

        <p className="profile-bio">{me?.bio?.trim() || t("profile.noBio")}</p>

        <div className="link-list">
          <a className="link-row" href={hrefFor("profil", "edit")}>
            <Icon size={20}>
              <IconPen />
            </Icon>
            <span className="link-row-label">{t("profile.edit")}</span>
            <Icon size={16} color="var(--color-text-muted)">
              <IconChevronRight />
            </Icon>
          </a>
        </div>
        <p className="field-note">{t("profile.editHint")}</p>
      </section>

      <section className="section">
        <h2 className="section-title">{t("profile.features")}</h2>
        <div className="link-list">
          {FEATURES.map((feature) => (
            <a
              key={`${feature.route}-${feature.param ?? ""}`}
              className="link-row"
              href={hrefFor(feature.route, feature.param)}
            >
              <Icon size={20}>{feature.glyph}</Icon>
              <span className="link-row-label">{t(feature.labelKey)}</span>
              <Icon size={16} color="var(--color-text-muted)">
                <IconChevronRight />
              </Icon>
            </a>
          ))}
        </div>

        <button type="button" className="btn btn-quiet btn-block" onClick={() => void signOut()}>
          <Icon size={18}>
            <IconLogout />
          </Icon>
          {t("auth.signOut")}
        </button>
      </section>
    </div>
  );
}
